// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: simplified Jobwork / Service popup.
//
// Horizontal label layout matching the Glass screenshot reference.
// Maps onto inventory_items + item_jobwork_specs. Defaults force
// is_service=true, is_goods=false, is_stock_item=false, is_jobwork_item=true.
// ─────────────────────────────────────────────────────────────────

import { Col, Form, Input, InputNumber, Select } from 'antd';
import SimpleCreateModalShell from './SimpleCreateModalShell';
import TripleSalesRateInput from './TripleSalesRateInput';
import {
  SIMPLE_KINDS,
  JOBWORK_SERVICE_OPTIONS,
  JOBWORK_CHARGE_BASIS_OPTIONS,
  TAXABILITY_OPTIONS
} from '../../simpleCreateConstants';
import { UomSelect, GstRateSelect } from './simpleCreateFields';

const GST_RULE = {
  validator: (_, v) =>
    v == null || v === '' || (Number(v) >= 0 && Number(v) <= 100)
      ? Promise.resolve()
      : Promise.reject(new Error('GST must be between 0 and 100'))
};
const PRICE_RULE = {
  validator: (_, v) =>
    v == null || v === '' || Number(v) >= 0
      ? Promise.resolve()
      : Promise.reject(new Error('Service Rate cannot be negative'))
};

export default function CreateJobworkModal({
  open, onClose, onSaved, editRecord = null, initialValues: initialValuesProp = null,
  viewOnly = false
}) {
  const initialValues = (editRecord || viewOnly) && initialValuesProp
    ? initialValuesProp
    : { taxabilityType: 'taxable', salesRate: 0 };
  return (
    <SimpleCreateModalShell
      kind={SIMPLE_KINDS.JOBWORK}
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      editRecord={editRecord}
      viewOnly={viewOnly}
      initialValues={initialValues}
      render={({ form }) => (
        <>
          {/* Row 1 — Service Name + Service Type */}
          <Col xs={24} md={12}>
            <Form.Item
              label="Service Name"
              name="itemName"
              required
              rules={[{ required: true, message: 'Service Name is required' }]}
            >
              <Input placeholder="e.g., Toughening — 6mm Clear" autoFocus />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Service Type" name="serviceTypeLabel">
              <Select
                placeholder="Select or type new…"
                allowClear
                showSearch
                options={JOBWORK_SERVICE_OPTIONS.map((o) => ({ value: o.label, label: o.label }))}
              />
            </Form.Item>
          </Col>

          {/* Row 2 — Label Name (full width) */}
          <Col xs={24}>
            <Form.Item label="Label Name" name="labelDesc">
              <Input placeholder="Display label name" />
            </Form.Item>
          </Col>

          {/* Row 3 — Charge Basis + UOM */}
          <Col xs={24} md={12}>
            <Form.Item label="Charge Basis" name="chargeBasisLabel">
              <Select
                placeholder="Select or type new…"
                allowClear
                options={JOBWORK_CHARGE_BASIS_OPTIONS.map((o) => ({ value: o.label, label: o.label }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="UOM" name="defaultUomId"
              rules={[{ required: true, message: 'UOM is required' }]}>
              <UomSelect placeholder="Select UOM (SQM, PCS, MTR…)" />
            </Form.Item>
          </Col>

          {/* Row 4 — SAC Code + GST */}
          <Col xs={24} md={12}>
            <Form.Item label="SAC Code" name="hsncode">
              <Input maxLength={12} placeholder="e.g., 998822" />
            </Form.Item>
          </Col>
          <Form.Item name="gstRatePercent" hidden><Input /></Form.Item>
          <Col xs={24} md={12}>
            <Form.Item label="GST (%)" name="gstRateId"
              rules={[{ required: true, message: 'GST rate is required' }]}>
              <GstRateSelect form={form} />
            </Form.Item>
          </Col>

          {/* Row 5 — Service Rate (triple: Sell / Min / Max) + Taxability */}
          <Form.Item name="minSalesRate" hidden><Input /></Form.Item>
          <Form.Item name="maxSalesRate" hidden><Input /></Form.Item>
          <Col xs={24} md={17}>
            <Form.Item
              label="Service Rate"
              name="salesRate"
              rules={[{ required: true, message: 'Service Rate is required' }, PRICE_RULE]}
            >
              <TripleSalesRateInput form={form} />
            </Form.Item>
          </Col>
          <Col xs={24} md={7}>
            <Form.Item
              label="Taxability"
              name="taxabilityType"
              labelCol={{ flex: '76px' }}
              wrapperCol={{ flex: 'auto' }}
            >
              <Select options={TAXABILITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} />
            </Form.Item>
          </Col>

          {/* Row 6 — Remarks (full width) */}
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={2} maxLength={500} placeholder="Add any additional notes here..." />
            </Form.Item>
          </Col>
        </>
      )}
    />
  );
}
